
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, UserCog } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { RoleCheckbox } from "./RoleCheckbox";
import { AssignSpecialistDialog } from "./AssignSpecialistDialog";

export type User = {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  roles: string[];
  user_name: string;
  specialist_name?: string;
  specialist_id?: string;
  caregiver_id?: string;
  usage_count?: number;
};

interface UserRoleTableProps {
  users: User[];
}

export function UserRoleTable({ users }: UserRoleTableProps) {
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [localUsers, setLocalUsers] = useState<User[]>(users);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Update local state when props change
  useEffect(() => {
    setLocalUsers(users);
  }, [users]);

  // Toggle role for a user
  const toggleRole = async (userId: string, role: string, currentRoles: string[]) => {
    setSavingUserId(userId);
    
    console.log("=== TOGGLE ROLE START ===");
    console.log("User ID:", userId);
    console.log("Role to toggle:", role);
    console.log("Current roles:", currentRoles);
    
    try {
      let newRoles;
      if (currentRoles.includes(role)) {
        // Remove the role
        newRoles = currentRoles.filter((r) => r !== role);
        console.log("Removing role, new roles:", newRoles);
      } else {
        // Add the role
        newRoles = [...currentRoles, role];
        console.log("Adding role, new roles:", newRoles);
      }

      console.log("Calling update_user_roles with:", { p_user_id: userId, p_roles: newRoles });
      
      const { data, error } = await supabase.rpc("update_user_roles", {
        p_user_id: userId,
        p_roles: newRoles,
      } as any);

      console.log("Response data:", data);
      console.log("Response error:", error);

      if (error) {
        throw error;
      }

      // Update the local state to reflect changes immediately
      setLocalUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { ...user, roles: newRoles } 
            : user
        )
      );

      console.log("Local state updated successfully");
      console.log("=== TOGGLE ROLE SUCCESS ===");

      toast({
        title: "Success",
        description: `Roles updated for ${users.find(u => u.id === userId)?.email}`,
      });
    } catch (error) {
      console.error("=== TOGGLE ROLE ERROR ===");
      console.error("Error updating roles:", error);
      toast({
        title: "Error",
        description: "Failed to update user roles",
        variant: "destructive",
      });
    } finally {
      setSavingUserId(null);
    }
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Usage</TableHead>
            <TableHead>Last Sign In</TableHead>
            <TableHead>Roles</TableHead>
            <TableHead>Assigned Specialist</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {localUsers.map((user) => (
            <TableRow key={user.id}>
              <TableCell className="font-medium">{user.user_name}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                <span className={`px-2 py-1 text-xs font-medium rounded-md ${
                  (user.usage_count || 0) > 50 
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400' 
                    : (user.usage_count || 0) > 10 
                    ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {user.usage_count || 0}
                </span>
              </TableCell>
              <TableCell>
                {user.last_sign_in_at
                  ? new Date(user.last_sign_in_at).toLocaleDateString()
                  : "Never"}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-4">
                    {["admin", "specialist", "caregiver"].map((role) => (
                      <RoleCheckbox
                        key={role}
                        userId={user.id}
                        role={role}
                        isChecked={user.roles.includes(role)}
                        isSaving={savingUserId === user.id}
                        onToggle={() => toggleRole(user.id, role, user.roles)}
                      />
                    ))}
                  </div>
                  {user.roles.length >= 2 && (
                    <span className="px-2 py-1 text-xs font-medium bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20 rounded-md whitespace-nowrap">
                      {user.roles.length} roles
                    </span>
                  )}
                  {savingUserId === user.id && (
                    <Loader2 className="h-4 w-4 animate-spin text-primary ml-2" />
                  )}
                </div>
              </TableCell>
              <TableCell>
                {user.roles.includes('caregiver') ? (
                  user.specialist_name ? (
                    <span className="text-sm text-muted-foreground">
                      {user.specialist_name}
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground italic">
                      Unassigned
                    </span>
                  )
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell>
                {user.roles.includes('caregiver') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedUser(user);
                      setAssignDialogOpen(true);
                    }}
                  >
                    <UserCog className="h-4 w-4 mr-2" />
                    {user.specialist_name ? 'Reassign' : 'Assign'}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {selectedUser && (
        <AssignSpecialistDialog
          open={assignDialogOpen}
          onOpenChange={setAssignDialogOpen}
          user={selectedUser}
          onSuccess={() => {
            // Refresh the users list
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}
